import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Loader2,
  Plus,
  Building2,
  MapPin,
  Truck,
  Calendar,
  FileText,
  User,
  Phone,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';
import TenantMobileLayout from './TenantMobileLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../components/ui/sheet';

const AUSTRALIAN_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const TenantMobileCreateJob = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Reference data
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    customer_id: '',
    site_id: '',
    equipment_type: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    start_time: '07:00',
    description: '',
    notes: '',
    status: 'not_allocated'
  });

  // Quick Add Customer state
  const [showCustomerSheet, setShowCustomerSheet] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: ''
  });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Quick Add Site state
  const [showSiteSheet, setShowSiteSheet] = useState(false);
  const [siteForm, setSiteForm] = useState({
    name: '',
    street_address: '',
    suburb: '',
    state: '',
    postcode: '',
    notes: ''
  });
  const [savingSite, setSavingSite] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, sitesRes, typesRes] = await Promise.all([
        api.get('/customers'),
        api.get('/sites'),
        api.get('/equipment-types')
      ]);
      
      setCustomers(customersRes.data || []);
      setSites(sitesRes.data || []);
      setEquipmentTypes(typesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerSites = (customerId) => {
    return sites.filter(s => s.customer_id === customerId);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || '';
  };

  const handleCustomerChange = (value) => {
    if (value === '__add_new__') {
      setCustomerForm({ company_name: '', contact_name: '', phone: '', email: '' });
      setShowCustomerSheet(true);
    } else {
      setForm({ ...form, customer_id: value, site_id: '' });
    }
  };

  const handleSiteChange = (value) => {
    if (value === '__add_new__') {
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });
      setShowSiteSheet(true);
    } else {
      setForm({ ...form, site_id: value });
    }
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!customerForm.contact_name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    setSavingCustomer(true);
    try {
      const response = await api.post('/customers', customerForm);
      const newCustomer = response.data;
      toast.success('Customer created');
      setShowCustomerSheet(false);
      
      // Refresh customers and auto-select the new one
      const customersRes = await api.get('/customers');
      setCustomers(customersRes.data);
      setForm({ ...form, customer_id: newCustomer.id, site_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleCreateSite = async () => {
    if (!siteForm.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    if (!siteForm.street_address.trim()) {
      toast.error('Street address is required');
      return;
    }
    if (!siteForm.suburb.trim()) {
      toast.error('Suburb is required');
      return;
    }
    if (!siteForm.state) {
      toast.error('State is required');
      return;
    }
    if (!siteForm.postcode.trim()) {
      toast.error('Postcode is required');
      return;
    }

    setSavingSite(true);
    try {
      const response = await api.post('/sites', {
        ...siteForm,
        customer_id: form.customer_id
      });
      const newSite = response.data;
      toast.success('Site created');
      setShowSiteSheet(false);
      
      // Refresh sites and auto-select the new one
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.data);
      setForm({ ...form, site_id: newSite.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    } finally {
      setSavingSite(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!form.start_date) {
      toast.error('Please select a start date');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...form,
        end_date: form.end_date || null,
        start_time: form.start_time || null,
        site_id: form.site_id || null
      };
      
      const response = await api.post('/jobs', submitData);
      toast.success('Job created');
      navigate(`/tenant-mobile/jobs/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <TenantMobileLayout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </TenantMobileLayout>
    );
  }

  const customerSites = getCustomerSites(form.customer_id);

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/tenant-mobile/jobs')} 
            className="text-gray-400 p-2"
            data-testid="back-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-white font-bold text-lg">Create Job</h1>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Customer Selection */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              Customer *
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={form.customer_id}
              onValueChange={handleCustomerChange}
            >
              <SelectTrigger 
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="customer-select"
              >
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-gray-700 max-h-64">
                <SelectItem value="__add_new__" className="text-[#ffd108]">
                  <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Add New Customer
                  </span>
                </SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Site Selection */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              Site
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={form.site_id}
              onValueChange={handleSiteChange}
              disabled={!form.customer_id}
            >
              <SelectTrigger 
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="site-select"
              >
                <SelectValue placeholder={form.customer_id ? "Select site" : "Select customer first"} />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-gray-700 max-h-64">
                {form.customer_id && (
                  <SelectItem value="__add_new__" className="text-[#ffd108]">
                    <span className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add New Site
                    </span>
                  </SelectItem>
                )}
                {customerSites.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.customer_id && customerSites.length === 0 && (
              <p className="text-gray-400 text-xs mt-2">No sites for this customer yet</p>
            )}
          </CardContent>
        </Card>

        {/* Equipment Type */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Truck className="h-5 w-5 text-[#ffd108]" />
              Equipment Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={form.equipment_type}
              onValueChange={(v) => setForm({ ...form, equipment_type: v })}
            >
              <SelectTrigger 
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="equipment-type-select"
              >
                <SelectValue placeholder="Select equipment type" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-gray-700 max-h-64">
                {equipmentTypes.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#ffd108]" />
              Schedule *
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">End Date</Label>
                <Input
                  type="date"
                  value={form.end_date || ''}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                  min={form.start_date}
                  data-testid="end-date-input"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Start Time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                data-testid="start-time-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Description *
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-[#1a1a1a] border-gray-700 text-white"
              rows={3}
              placeholder="Job description..."
              data-testid="description-input"
            />
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-500" />
              Notes (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-[#1a1a1a] border-gray-700 text-white"
              rows={2}
              placeholder="Additional notes..."
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          className="w-full btn-volt py-6"
          onClick={handleSubmit}
          disabled={saving}
          data-testid="create-job-btn"
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <Plus className="h-5 w-5 mr-2" />
          )}
          Create Job
        </Button>
      </div>

      {/* Quick Add Customer Sheet */}
      <Sheet open={showCustomerSheet} onOpenChange={setShowCustomerSheet}>
        <SheetContent side="bottom" className="bg-[#2a2a2a] border-gray-700 h-[85vh]">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              Add New Customer
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Company Name *
              </Label>
              <Input
                value={customerForm.company_name}
                onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })}
                placeholder="ABC Construction"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-customer-company"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase flex items-center gap-1">
                <User className="h-3 w-3" />
                Contact Name *
              </Label>
              <Input
                value={customerForm.contact_name}
                onChange={(e) => setCustomerForm({ ...customerForm, contact_name: e.target.value })}
                placeholder="John Smith"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-customer-contact"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Phone
              </Label>
              <Input
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                placeholder="0412 345 678"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-customer-phone"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </Label>
              <Input
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="john@abc.com"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-customer-email"
              />
            </div>
          </div>

          <SheetFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              className="flex-1 border-gray-700"
              onClick={() => setShowCustomerSheet(false)}
              disabled={savingCustomer}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 btn-volt"
              onClick={handleCreateCustomer}
              disabled={savingCustomer}
              data-testid="quick-customer-submit"
            >
              {savingCustomer ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Customer
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Quick Add Site Sheet */}
      <Sheet open={showSiteSheet} onOpenChange={setShowSiteSheet}>
        <SheetContent side="bottom" className="bg-[#2a2a2a] border-gray-700 h-[90vh]">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              Add New Site
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto">
            {/* Customer context */}
            <div className="p-3 bg-[#1a1a1a] rounded-lg">
              <p className="text-sm text-gray-400">
                Adding site for: <span className="font-medium text-white">{getCustomerName(form.customer_id)}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Site Name *</Label>
              <Input
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder="Main Office, Warehouse, etc."
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-site-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Street Address *</Label>
              <Input
                value={siteForm.street_address}
                onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                placeholder="123 Main Street"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-site-street"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Suburb *</Label>
              <Input
                value={siteForm.suburb}
                onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                placeholder="Melbourne"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-site-suburb"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">State *</Label>
                <Select 
                  value={siteForm.state} 
                  onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white" data-testid="quick-site-state">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-700">
                    {AUSTRALIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Postcode *</Label>
                <Input
                  value={siteForm.postcode}
                  onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                  placeholder="3000"
                  maxLength={4}
                  className="bg-[#1a1a1a] border-gray-700 text-white"
                  data-testid="quick-site-postcode"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Notes</Label>
              <Textarea
                value={siteForm.notes}
                onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                placeholder="Access instructions, parking info..."
                rows={2}
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="quick-site-notes"
              />
            </div>
          </div>

          <SheetFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              className="flex-1 border-gray-700"
              onClick={() => setShowSiteSheet(false)}
              disabled={savingSite}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 btn-volt"
              onClick={handleCreateSite}
              disabled={savingSite}
              data-testid="quick-site-submit"
            >
              {savingSite ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Site
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </TenantMobileLayout>
  );
};

export default TenantMobileCreateJob;
